import Raven from 'raven-js';
import { useMemo } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import WorkflowName from 'automation-ui-references/customer-data/WorkflowName';
import ObjectListName from 'automation-ui-references/customer-data/ObjectListName';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { ACTION_TYPE_TO_METADATA } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { INBOUND_ACTION_TYPES, INBOUND_REFERENCE_SOURCE_TYPES, SUPPRESSION_REFERENCE_ACTION_TYPE } from 'automation-ui-management/constants/WorkflowReferenceConstants';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InboundFlowReferencesSection = ({
  inboundReferences
}) => {
  const actionRefs = useMemo(() => {
    return inboundReferences.filter(ref => ref.referenceSourceType === INBOUND_REFERENCE_SOURCE_TYPES.ACTION);
  }, [inboundReferences]);
  const listRefs = useMemo(() => {
    return inboundReferences.filter(ref => ref.referenceSourceType === INBOUND_REFERENCE_SOURCE_TYPES.LIST);
  }, [inboundReferences]);
  const branchCriteriaRefs = useMemo(() => {
    return actionRefs.filter(ref => ref.actionType === ActionTypes.LIST_BRANCH);
  }, [actionRefs]);
  const enrollmentCriteriaRefs = useMemo(() => {
    return actionRefs.filter(ref => ref.actionType === INBOUND_ACTION_TYPES.ENROLLMENT);
  }, [actionRefs]);
  const goalCriteriaRefs = useMemo(() => {
    return actionRefs.filter(ref => ref.actionType === INBOUND_ACTION_TYPES.GOAL_CRITERIA);
  }, [actionRefs]);
  const suppressionRefs = useMemo(() => {
    return actionRefs.filter(ref => ref.actionType === SUPPRESSION_REFERENCE_ACTION_TYPE);
  }, [actionRefs]);
  const trackEvent = useTrackEvent();
  const trackAccordionChange = (isOpen, key) => {
    if (isOpen) {
      trackEvent(Trackables.VIEWED_REFERENCE_TYPE, {
        assetType: key,
        referenceType: 'inbound'
      });
    }
  };
  const renderExpandableReferenceType = (refs, title, key, styled = true) => {
    if (!refs.length) {
      return /*#__PURE__*/_jsx(View, {
        marginInlineStart: "400",
        marginBlockEnd: "200",
        as: "div",
        children: title
      });
    }
    return /*#__PURE__*/_jsx(UIAccordionItem, {
      onOpenChange: event => trackAccordionChange(event.target.value, key),
      size: "extra-small",
      title: title,
      children: /*#__PURE__*/_jsx(UIList, {
        styled: styled,
        children: refs
      })
    }, key);
  };
  const renderActionLinks = refs => {
    const actionRefsByFlowId = refs.reduce((flowToActionRefMap, ref) => {
      const isSupportedAction = !!ACTION_TYPE_TO_METADATA[ref.actionType];
      if (isSupportedAction) {
        if (flowToActionRefMap[ref.flowId]) {
          flowToActionRefMap[ref.flowId].push(ref);
        } else {
          flowToActionRefMap[ref.flowId] = [ref];
        }
      }
      if (!isSupportedAction) {
        Raven.captureMessage(`Unsupported inbound action refrerence type: ${ref.actionType}`, {
          level: 'warning'
        });
      }
      return flowToActionRefMap;
    }, {});
    return Object.keys(actionRefsByFlowId).map(flowId => {
      const actionRefsForFlow = actionRefsByFlowId[flowId];
      const actionLinks = actionRefsForFlow.map(actionRef => /*#__PURE__*/_jsx(UILink, {
        external: true,
        href: actionRef.referenceUrl || undefined,
        children: ACTION_TYPE_TO_METADATA[actionRef.actionType].generalDescription
      }, `${flowId}-${actionRef.actionId}`));
      return renderExpandableReferenceType(actionLinks, /*#__PURE__*/_jsx(WorkflowName, {
        flowSourceType: actionRefsForFlow[0].flowSource,
        id: flowId
      }), INBOUND_REFERENCE_SOURCE_TYPES.FLOW);
    });
  };
  const renderListLinks = refs => {
    return refs.map(ref => {
      return /*#__PURE__*/_jsx(ObjectListName, {
        id: ref.listId
      }, ref.listId);
    });
  };
  const renderFlowLinks = refs => {
    return refs.map(ref => {
      return /*#__PURE__*/_jsx(WorkflowName, {
        flowSourceType: ref.flowSource,
        id: ref.flowId,
        href: ref.referenceSourceType === INBOUND_REFERENCE_SOURCE_TYPES.ACTION ? ref.referenceUrl : undefined
      }, ref.flowId);
    });
  };
  const actionReferenceLinks = renderActionLinks(actionRefs);
  const listReferenceLinks = renderListLinks(listRefs);
  const branchCriteriaLinks = renderFlowLinks(branchCriteriaRefs);
  const enrollmentCriteriaLinks = renderFlowLinks(enrollmentCriteriaRefs);
  const goalCriteriaLinks = renderFlowLinks(goalCriteriaRefs);
  const suppressionLinks = renderFlowLinks(suppressionRefs);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "inbound-flow-references-section",
    children: [renderExpandableReferenceType(actionReferenceLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.actions",
      options: {
        count: actionReferenceLinks.length
      }
    }), INBOUND_REFERENCE_SOURCE_TYPES.ACTION, false), renderExpandableReferenceType(listReferenceLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.lists",
      options: {
        count: listReferenceLinks.length
      }
    }), INBOUND_REFERENCE_SOURCE_TYPES.LIST), renderExpandableReferenceType(branchCriteriaLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.branchCriteria",
      options: {
        count: branchCriteriaLinks.length
      }
    }), INBOUND_ACTION_TYPES.LIST_BRANCH), renderExpandableReferenceType(enrollmentCriteriaLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.enrollmentCriteria",
      options: {
        count: enrollmentCriteriaLinks.length
      }
    }), INBOUND_ACTION_TYPES.ENROLLMENT), renderExpandableReferenceType(goalCriteriaLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.goalCriteria",
      options: {
        count: goalCriteriaLinks.length
      }
    }), INBOUND_ACTION_TYPES.GOAL_CRITERIA), renderExpandableReferenceType(suppressionLinks, /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.references.referenceTitles.suppression",
      options: {
        count: suppressionLinks.length
      }
    }), SUPPRESSION_REFERENCE_ACTION_TYPE)]
  });
};
export default InboundFlowReferencesSection;